/**
 * Demo-only "Custom brand artifacts" card.
 *
 * Lets a demoer swap the demo tenant's Brand Bible / brief template /
 * compliance files with a buyer's actual artifacts before a meeting.
 * Everything written here lands in `profileSettings.guidelines` on the
 * demo enterprise — the exact same struct that:
 *   - rembrandt's `get_review_context_activity` reads as `text_guidelines`
 *     (every persona prompt grounds its findings against it)
 *   - samba's `GuidelinesProcessingWorkflow` re-embeds into Bedrock and
 *     writes to `aiguidelines`, which the Ask Puntt RAG path reads back
 *
 * That means:
 *   - Real-AI pre-run reviews (Phase 2 of 3-tier asset flow) automatically
 *     cite the uploaded artifacts because they read the same field.
 *   - Off-script Ask Puntt reviews (drops by a prospect mid-demo) get
 *     grounded against the uploaded artifacts via vector search.
 *   - "Reset Demo" (above this card) wipes `aiguidelines` + every demo
 *     mutation and re-stamps the canonical Vitalora seed — bringing the
 *     buyer's customisations all the way back to the blank Vitalora canon.
 *
 * Gated by `isDemoEnterprise()` at the call site, never renders on a
 * real tenant.
 */
import { type EnterpriseBriefGuidelines } from "@mg/schemas/src/commons/enterprise";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Separator,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import { useUI } from "../../../contexts/ui";
import {
  getGuidelines,
  updateGuidelines,
} from "../../../services/brand-guidelines";
import { uploadFile } from "../../../services/upload";
import { errorMessage } from "../../../utils/http";

type AssetEntry = {
  source: string;
  originalSource?: string;
  name?: string;
  type?: "file" | "image";
};

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "done"; at: string }
  | { kind: "error"; message: string };

const SECTION_HELP: Record<string, string> = {
  bible:
    "This is the rulebook the AI reviewers use to judge new work. Paste the " +
    "buyer's actual voice, tone, approved claims, and visual rules here. Be " +
    'specific and add section numbers when you can (e.g. "Section 3.2: ' +
    'Logo placement requires…") so the AI can quote the exact rule when it ' +
    "flags something. If you leave this blank or write very little, the AI " +
    "won't have anything to compare against and you'll get no findings.",
  brief:
    "Optional. Add the buyer's brief template (the form their team fills out " +
    "when they start a new project) and a checklist of things every brief " +
    "must include. Leave this blank if the buyer doesn't want the AI to " +
    "review their briefs.",
};

export function DemoBrandArtifactsCard() {
  const { notify } = useUI();
  const queryClient = useQueryClient();
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });

  const [description, setDescription] = useState("");
  const [summary, setSummary] = useState("");
  const [checklist, setChecklist] = useState("");
  const [files, setFiles] = useState<AssetEntry[]>([]);
  const [templates, setTemplates] = useState<AssetEntry[]>([]);
  const [bibleSave, setBibleSave] = useState<SaveState>({ kind: "idle" });
  const [briefSave, setBriefSave] = useState<SaveState>({ kind: "idle" });
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isUploadingTemplates, setIsUploadingTemplates] = useState(false);
  const filesInputRef = useRef<HTMLInputElement>(null);
  const templatesInputRef = useRef<HTMLInputElement>(null);
  const [hydrated, setHydrated] = useState(false);

  const remote: EnterpriseBriefGuidelines | undefined =
    guidelinesQuery.data?.guidelines;

  // Hydrate local state once when the server payload first arrives.
  // Subsequent updates from the query cache (e.g. after a save) re-run
  // this effect but the guard prevents overwriting in-flight edits.
  useEffect(() => {
    if (!remote || hydrated) return;
    setDescription(remote.description ?? "");
    setSummary(remote.summary ?? "");
    setChecklist(remote.checklist ?? "");
    setFiles((remote.files ?? []) as AssetEntry[]);
    setTemplates((remote.templates ?? []) as AssetEntry[]);
    setHydrated(true);
  }, [remote, hydrated]);

  const dirtyBible = useMemo(() => {
    if (!remote) return false;
    return (
      description !== (remote.description ?? "") ||
      summary !== (remote.summary ?? "") ||
      JSON.stringify(files) !==
        JSON.stringify(((remote.files ?? []) as AssetEntry[]) || [])
    );
  }, [description, summary, files, remote]);

  const dirtyBrief = useMemo(() => {
    if (!remote) return false;
    return (
      checklist !== (remote.checklist ?? "") ||
      JSON.stringify(templates) !==
        JSON.stringify(((remote.templates ?? []) as AssetEntry[]) || [])
    );
  }, [checklist, templates, remote]);

  async function persist(
    payload: EnterpriseBriefGuidelines,
    setState: (s: SaveState) => void,
    successMessage: string,
  ) {
    setState({ kind: "saving" });
    try {
      // Server merges with existing values, so we send the full struct
      // (description + files + templates + checklist + summary) for both
      // saves. That keeps the "Brief" save from accidentally clobbering
      // a long Brand Bible description.
      const merged: EnterpriseBriefGuidelines = {
        description,
        summary,
        checklist,
        files,
        templates,
        ...payload,
      };
      await updateGuidelines(merged);
      await queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
      setState({ kind: "done", at: new Date().toISOString() });
      notify({
        title: successMessage,
        message:
          "Saved. The AI will start using these files in new reviews within about 30 seconds.",
        variant: "success",
      });
    } catch (e) {
      setState({ kind: "error", message: await errorMessage(e) });
    }
  }

  async function handleUpload(
    event: ChangeEvent<HTMLInputElement>,
    bucket: "files" | "templates",
  ) {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const setBusy =
      bucket === "files" ? setIsUploadingFiles : setIsUploadingTemplates;
    const inputRef = bucket === "files" ? filesInputRef : templatesInputRef;

    setBusy(true);
    try {
      const payloads = await Promise.all(
        Array.from(fileList).map((f) => uploadFile(f)),
      );
      const newAssets: AssetEntry[] = payloads.map((p, i) => ({
        source: `https://static.puntt.ai/${p.key}`,
        originalSource: fileList[i]?.name,
        name: fileList[i]?.name,
        type: fileList[i]?.type?.includes("image") ? "image" : "file",
      }));
      if (bucket === "files") {
        setFiles((prev) => [...prev, ...newAssets]);
      } else {
        setTemplates((prev) => [...prev, ...newAssets]);
      }
      notify({
        title: `${newAssets.length} file${newAssets.length === 1 ? "" : "s"} uploaded`,
        message: "Click Save below to keep them.",
        variant: "success",
      });
    } catch (e) {
      notify({
        title: "Upload failed",
        message: await errorMessage(e),
        variant: "error",
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function removeFile(bucket: "files" | "templates", idx: number) {
    if (bucket === "files") {
      setFiles((prev) => prev.filter((_, i) => i !== idx));
    } else {
      setTemplates((prev) => prev.filter((_, i) => i !== idx));
    }
  }

  if (guidelinesQuery.isLoading) {
    return (
      <Card data-testid="settings-card-demo-brand-artifacts">
        <Heading as="h2" size="3" weight="bold">
          Buyer&apos;s brand and brief files
        </Heading>
        <Text size="2" color="gray">
          Loading…
        </Text>
      </Card>
    );
  }

  return (
    <Card data-testid="settings-card-demo-brand-artifacts">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Buyer&apos;s brand and brief files
          </Heading>
          <Text size="2" color="gray">
            Before a meeting, replace the demo&apos;s default brand book, brief
            template, and compliance files with the buyer&apos;s real ones. The
            AI will then judge their work against their own rules, so the
            findings sound like something their own team would catch. Click{" "}
            <strong>Reset Demo</strong> above to wipe these and go back to the
            default Vitalora files.
          </Text>
        </Flex>

        {/* Brand book section */}
        <Flex direction="column" gap="2">
          <Heading as="h3" size="2" weight="bold">
            Brand book
          </Heading>
          <Text size="1" color="gray">
            {SECTION_HELP.bible}
          </Text>
          <TextArea
            data-testid="demo-artifacts-bible-description"
            placeholder="Paste the buyer's brand book here. Voice, tone, logo rules, approved claims. Use section numbers when you can."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ minHeight: 200, fontFamily: "ui-monospace, monospace" }}
          />
          <TextArea
            data-testid="demo-artifacts-bible-summary"
            placeholder="Optional. A short one-line summary, shown in the Knowledge Hub."
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            style={{ minHeight: 60 }}
          />

          <FileBucketList
            label="Other reference files (PDFs, brand books, regulatory checklists, risk guides)"
            files={files}
            onRemove={(idx) => removeFile("files", idx)}
          />

          <Flex align="center" gap="3" wrap="wrap">
            <Button
              data-testid="demo-artifacts-bible-upload"
              variant="soft"
              disabled={isUploadingFiles}
              onClick={() => filesInputRef.current?.click()}
            >
              {isUploadingFiles ? "Uploading…" : "Add reference files"}
            </Button>
            <input
              ref={filesInputRef}
              data-testid="demo-artifacts-bible-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md,image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleUpload(e, "files")}
            />
            <Button
              data-testid="demo-artifacts-bible-save"
              disabled={!dirtyBible || bibleSave.kind === "saving"}
              onClick={() =>
                persist(
                  { description, summary, files },
                  setBibleSave,
                  "Brand Bible saved",
                )
              }
            >
              {bibleSave.kind === "saving" ? "Saving…" : "Save brand book"}
            </Button>
            <SaveStatus state={bibleSave} />
          </Flex>
        </Flex>

        <Separator size="4" />

        {/* Brief template + checklist section */}
        <Flex direction="column" gap="2">
          <Heading as="h3" size="2" weight="bold">
            Brief template and checklist
          </Heading>
          <Text size="1" color="gray">
            {SECTION_HELP.brief}
          </Text>
          <TextArea
            data-testid="demo-artifacts-brief-checklist"
            placeholder={
              "One checklist item per line. Things every brief must cover, for example:\nTarget audience\nMarket\nApproved claims"
            }
            value={checklist}
            onChange={(e) => setChecklist(e.target.value)}
            style={{ minHeight: 120, fontFamily: "ui-monospace, monospace" }}
          />

          <FileBucketList
            label="Brief template files"
            files={templates}
            onRemove={(idx) => removeFile("templates", idx)}
          />

          <Flex align="center" gap="3" wrap="wrap">
            <Button
              data-testid="demo-artifacts-brief-upload"
              variant="soft"
              disabled={isUploadingTemplates}
              onClick={() => templatesInputRef.current?.click()}
            >
              {isUploadingTemplates ? "Uploading…" : "Add brief template files"}
            </Button>
            <input
              ref={templatesInputRef}
              data-testid="demo-artifacts-brief-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleUpload(e, "templates")}
            />
            <Button
              data-testid="demo-artifacts-brief-save"
              disabled={!dirtyBrief || briefSave.kind === "saving"}
              onClick={() =>
                persist(
                  { checklist, templates },
                  setBriefSave,
                  "Brief template saved",
                )
              }
            >
              {briefSave.kind === "saving" ? "Saving…" : "Save brief template"}
            </Button>
            <SaveStatus state={briefSave} />
          </Flex>
        </Flex>

        <Separator size="4" />

        {/* Pointer to approved claims (lives in the Knowledge Hub) */}
        <Box>
          <Heading as="h3" size="2" weight="bold">
            Approved claims
          </Heading>
          <Text size="1" color="gray">
            The list of claims the buyer is allowed to make about each product
            (what the AI references when it flags an unsupported health claim)
            lives in the Knowledge Hub. Edit them on the{" "}
            <strong>Knowledge Settings</strong> page. They&apos;re wiped by
            Reset Demo the same way as the files here.
          </Text>
        </Box>
      </Flex>
    </Card>
  );
}

function FileBucketList({
  label,
  files,
  onRemove,
}: {
  label: string;
  files: AssetEntry[];
  onRemove: (idx: number) => void;
}) {
  if (files.length === 0) {
    return (
      <Text size="1" color="gray">
        {label}: <em>nothing uploaded yet</em>
      </Text>
    );
  }
  return (
    <Flex direction="column" gap="1">
      <Text size="1" color="gray">
        {label}:
      </Text>
      {files.map((f, idx) => {
        const name = f.name ?? f.originalSource ?? f.source?.split("/").pop();
        return (
          <Flex key={`${f.source}-${idx}`} align="center" gap="2">
            <Text size="2" style={{ flex: 1, wordBreak: "break-all" }}>
              {name}
            </Text>
            <Button
              variant="ghost"
              size="1"
              color="red"
              onClick={() => onRemove(idx)}
              data-testid={`demo-artifacts-remove-${idx}`}
            >
              Remove
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
}

function SaveStatus({ state }: { state: SaveState }) {
  if (state.kind === "done") {
    return (
      <Text size="1" color="green">
        Saved {new Date(state.at).toLocaleTimeString()}
      </Text>
    );
  }
  if (state.kind === "error") {
    return (
      <Text size="1" color="red">
        {state.message}
      </Text>
    );
  }
  return null;
}
